// Utilitário para calcular taxas do Stripe
export interface TaxCalculation {
  valorSolicitado: number;
  valorTotal: number;
  taxaPercentual: number;
  taxaFixa: number;
  taxaTotal: number;
  metodoPagamento: 'CARTAO' | 'PIX';
}

export class TaxCalculator {
  // Taxas do Stripe para cartões nacionais
  private static readonly TAXA_CARTAO_PERCENTUAL = 0.0399; // 3,99%
  private static readonly TAXA_CARTAO_FIXA = 0.39; // R$ 0,39

  // Taxa do Stripe para PIX
  private static readonly TAXA_PIX_PERCENTUAL = 0.0119; // 1,19%
  private static readonly TAXA_PIX_FIXA = 0; // R$ 0,00

  /**
   * Calcula as taxas do Stripe para um valor e método de pagamento
   * @param valorSolicitado - Valor que o cliente quer adicionar ao saldo
   * @param metodoPagamento - Método de pagamento escolhido
   * @returns Objeto com breakdown completo das taxas
   */
  static calcularTaxas(valorSolicitado: number, metodoPagamento: 'CARTAO' | 'PIX'): TaxCalculation {
    let taxaPercentual: number;
    let taxaFixa: number;

    if (metodoPagamento === 'CARTAO') {
      taxaPercentual = this.TAXA_CARTAO_PERCENTUAL;
      taxaFixa = this.TAXA_CARTAO_FIXA;
    } else {
      taxaPercentual = this.TAXA_PIX_PERCENTUAL;
      taxaFixa = this.TAXA_PIX_FIXA;
    }

    // Calcular taxa percentual
    const taxaPercentualValor = valorSolicitado * taxaPercentual;
    
    // Calcular taxa total
    const taxaTotal = taxaPercentualValor + taxaFixa;
    
    // Calcular valor total que será cobrado
    const valorTotal = valorSolicitado + taxaTotal;

    return {
      valorSolicitado,
      valorTotal: Math.round(valorTotal * 100) / 100, // Arredondar para 2 casas decimais
      taxaPercentual: taxaPercentual * 100, // Converter para porcentagem
      taxaFixa,
      taxaTotal: Math.round(taxaTotal * 100) / 100, // Arredondar para 2 casas decimais
      metodoPagamento
    };
  }

  /**
   * Formata valores monetários para exibição
   * @param valor - Valor a ser formatado
   * @returns String formatada em reais
   */
  static formatarValor(valor: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  }

  /**
   * Calcula o valor líquido (sem taxas) a partir do valor total
   * Útil para casos onde o usuário quer pagar um valor total específico
   * @param valorTotal - Valor total que o usuário quer pagar
   * @param metodoPagamento - Método de pagamento
   * @returns Valor líquido que será creditado
   */
  static calcularValorLiquido(valorTotal: number, metodoPagamento: 'CARTAO' | 'PIX'): number {
    let taxaPercentual: number;
    let taxaFixa: number;

    if (metodoPagamento === 'CARTAO') {
      taxaPercentual = this.TAXA_CARTAO_PERCENTUAL;
      taxaFixa = this.TAXA_CARTAO_FIXA;
    } else {
      taxaPercentual = this.TAXA_PIX_PERCENTUAL;
      taxaFixa = this.TAXA_PIX_FIXA;
    }

    // Fórmula: valorTotal = valorLiquido + (valorLiquido * taxaPercentual) + taxaFixa
    // Resolvendo para valorLiquido: valorLiquido = (valorTotal - taxaFixa) / (1 + taxaPercentual)
    const valorLiquido = (valorTotal - taxaFixa) / (1 + taxaPercentual);
    
    return Math.round(valorLiquido * 100) / 100;
  }
} 